import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CreditCard, Info, Loader2, Luggage } from "lucide-react";
import { RefreshCw } from "lucide-react";
import { ItineraryData } from "@/lib/builder/models/itinerary.model";
import { createStripePaymentLink } from "@/lib/itineraries/stripe-link";
import { useAuth } from '@/hooks/useAuth';

interface BookTripModalProps {
  isOpen: boolean;
  onClose: () => void;
  itinerary: ItineraryData | null;
}

export const BookTripModal: React.FC<BookTripModalProps> = ({ isOpen, onClose, itinerary }) => {
  const { user } = useAuth();
  const [stripeUrl, setStripeUrl] = useState<string | null>(null);
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showStripeInfo, setShowStripeInfo] = useState(false);

  const canCreateStripeLink = Boolean(
    itinerary?.id && itinerary?.travel_request_id && itinerary?.travel_request?.contact_email
  );

  const fetchStripeLink = useCallback(async (resetInfo = false) => {
    if (!itinerary || !canCreateStripeLink) return;
    setStripeUrl(null);
    setHasError(false);
    setIsCreatingLink(true);
    if (resetInfo) setShowStripeInfo(false);
    try {
      const url = await createStripePaymentLink({
        itineraryId: itinerary.id,
        travelRequestId: itinerary.travel_request_id,
        itineraryTitle: itinerary.title,
        itineraryTotal: itinerary.total,
        customerEmail: itinerary.travel_request?.contact_email,
        userId: itinerary.travel_request.user_id || user?.id,
      });
      setStripeUrl(url);
    } catch {
      setHasError(true);
    } finally {
      setIsCreatingLink(false);
    }
  }, [canCreateStripeLink, itinerary, user]);

  useEffect(() => {
    if (!isOpen) return;
    if (!canCreateStripeLink) return;
    if (!stripeUrl && !isCreatingLink && !hasError) {
      fetchStripeLink();
    }
  }, [isOpen, canCreateStripeLink]);

  const handleClose = () => {
    setTimeout(() => {
      setStripeUrl(null);
      setHasError(false);
      setIsCreatingLink(false);
      setShowStripeInfo(false);
    }, 1000);
    onClose();
  };

  const handlePayNow = () => {
    if (!stripeUrl) return;
    window.open(stripeUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => {
      setShowStripeInfo(true);
    }, 1000);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Book your trip">
      <div className="space-y-4 p-6">
        {showStripeInfo ? (
          <Alert variant="info">
            <Info className="h-4 w-4" />
            <AlertDescription>
              The Stripe link has been opened.<br />
              Please complete your payment on the Stripe page.<br />
              After payment, you will receive a confirmation email and your booking will be activated.
            </AlertDescription>
            <div className="mt-4 flex justify-center">
              <Button variant="outline" size="sm" onClick={() => fetchStripeLink(true)} disabled={isCreatingLink || !canCreateStripeLink}>
                {isCreatingLink ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate
                  </>
                )}
              </Button>
            </div>
          </Alert>
        ) : (
          <>
            <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-4">
              <Luggage className="h-5 w-5 text-gray-500" />
              <p className="text-sm text-gray-700">
                Complete your booking with a secure Stripe payment.
              </p>
            </div>
            {!canCreateStripeLink ? (
              <Alert variant="destructive">
                <AlertDescription>
                  We cannot create a payment link for this itinerary right now.
                </AlertDescription>
              </Alert>
            ) : hasError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  Error creating Stripe payment link. Please try again.
                </AlertDescription>
              </Alert>
            ) : isCreatingLink ? (
              <Alert variant="info">
                <Info className="h-4 w-4" />
                <AlertDescription>Creating Stripe payment link...</AlertDescription>
              </Alert>
            ) : stripeUrl ? (
              <Alert variant="info">
                <Info className="h-4 w-4" />
                <AlertDescription>Payment link ready. Continue to Stripe to complete payment.</AlertDescription>
              </Alert>
            ) : null}
          </>
        )}
        <div className="flex justify-between pt-2 gap-2">
          {showStripeInfo ? (
            <>
              <Button variant="ghost" size="sm" onClick={handleClose}>
                Chiudi
              </Button>
              <Button size="sm" onClick={() => window.location.href = '/dashboard'}>
                Torna alla dashboard
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={handleClose}>
                Close
              </Button>
              {hasError ? (
                <Button size="sm" onClick={fetchStripeLink} disabled={isCreatingLink || !canCreateStripeLink}>
                  {isCreatingLink ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    "Retry"
                  )}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handlePayNow}
                  disabled={!stripeUrl || isCreatingLink || !canCreateStripeLink}
                  className="flex items-center gap-2"
                >
                  {isCreatingLink ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      Pay now
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
  };
